import { useCallback, useEffect, useMemo, useState } from "react";
import FeaturedPostsCarousel from "../components/Blog/FeaturedPostsCarousel/FeaturedPostsCarousel";
import BlogPostEditor from "../components/Admin/BlogPostEditor/BlogPostEditor";
import { ConfirmDialog, SuccessDialog } from "../components/Admin/Dialogs";
import FormActions from "../components/Admin/FormActions/FormActions";
import {
  atualizarPost,
  buscarPostAdmin,
  criarPost,
  enviarImagem,
  excluirPost,
  listarPostsAdmin,
  ocultarPost,
  publicarPost,
  reexibirPost,
  removerImagem,
} from "../services/blog";
import { getImageUrl } from "../utils/media";
import "./AdminBlog.css";

const POSTS_PER_PAGE = 4;

const STATUS_LABELS = {
  draft: "Rascunho",
  published: "Publicado",
  hidden: "Oculto",
};

function formatDate(value) {
  if (!value) return "Sem publicacao";
  return new Intl.DateTimeFormat("pt-BR").format(new Date(value));
}

function getAuthorName(post) {
  return post?.author?.name || post?.authorName || "7 Geracoes";
}

function getErrorMessage(error, fallback) {
  return error?.response?.data?.error || fallback;
}

export default function AdminBlog() {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState("");
  const [page, setPage] = useState(1);
  const [editMode, setEditMode] = useState(false);
  const [editorPost, setEditorPost] = useState(undefined);
  const [editorLoading, setEditorLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [submitError, setSubmitError] = useState("");
  const [busyId, setBusyId] = useState("");
  const [feedback, setFeedback] = useState("");
  const [successMessage, setSuccessMessage] = useState("");
  const [confirmation, setConfirmation] = useState(null);

  const loadPosts = useCallback(async ({ showLoading = true } = {}) => {
    if (showLoading) setLoading(true);

    try {
      const loadedPosts = await listarPostsAdmin();
      setPosts(loadedPosts);
      setLoadError("");
      return loadedPosts;
    } catch (error) {
      if (!showLoading) throw error;
      setLoadError(getErrorMessage(error, "Nao foi possivel carregar os posts."));
      return [];
    } finally {
      if (showLoading) setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadPosts();
  }, [loadPosts]);

  const pageCount = Math.max(1, Math.ceil(posts.length / POSTS_PER_PAGE));
  const orderedPosts = useMemo(
    () =>
      [...posts].sort((firstPost, secondPost) => {
        const firstDate = new Date(
          firstPost.createdAt || firstPost.updatedAt,
        ).getTime();
        const secondDate = new Date(
          secondPost.createdAt || secondPost.updatedAt,
        ).getTime();

        return secondDate - firstDate;
      }),
    [posts],
  );
  const visiblePosts = orderedPosts.slice(
    (page - 1) * POSTS_PER_PAGE,
    page * POSTS_PER_PAGE,
  );
  const publishedPosts = useMemo(
    () => orderedPosts.filter((post) => post.status === "published"),
    [orderedPosts],
  );

  useEffect(() => {
    if (page > pageCount) setPage(pageCount);
  }, [page, pageCount]);

  const showFeedback = (message) => {
    setFeedback(message);
    window.setTimeout(() => setFeedback(""), 4000);
  };

  const openNewPost = () => {
    setSubmitError("");
    setEditorPost(null);
  };

  const openEditPost = async (post) => {
    setEditorLoading(true);
    setSubmitError("");
    try {
      setEditorPost(await buscarPostAdmin(post._id));
    } catch (error) {
      showFeedback(getErrorMessage(error, "Nao foi possivel abrir este post."));
    } finally {
      setEditorLoading(false);
    }
  };

  const closeEditor = () => {
    if (saving) return;
    setEditorPost(undefined);
    setSubmitError("");
  };

  const savePost = async ({
    imageFiles = [],
    removeImageIds = [],
    removedImagePublicIds = [],
    ...form
  }) => {
    setSaving(true);
    setSubmitError("");
    let uploadedImages = [];

    try {
      uploadedImages = await Promise.all(imageFiles.map((file) => enviarImagem(file)));

      if (editorPost) {
        const payload = { ...form };

        if (uploadedImages.length > 0) {
          payload.images = uploadedImages;
        }

        if (removeImageIds.length > 0) {
          payload.removeImageIds = removeImageIds;
        }

        await atualizarPost(editorPost._id, payload);

        removedImagePublicIds.forEach((publicId) =>
          removerImagem(publicId).catch(() => undefined),
        );
      } else {
        await criarPost({ ...form, images: uploadedImages });
      }

      const wasEditing = Boolean(editorPost);
      setEditorPost(undefined);
      await loadPosts({ showLoading: false });
      if (!wasEditing) {
        setPage(1);
      }
      setSuccessMessage(
        wasEditing
          ? "Post atualizado com sucesso!"
          : "Rascunho criado com sucesso!",
      );
    } catch (error) {
      uploadedImages.forEach((image) => {
        if (image?.public_id) removerImagem(image.public_id).catch(() => undefined);
      });
      setSubmitError(getErrorMessage(error, "Nao foi possivel salvar o post."));
    } finally {
      setSaving(false);
    }
  };

  const runStatusAction = async (post, action) => {
    setBusyId(post._id);
    try {
      if (action === "publish") await publicarPost(post._id);
      if (action === "unhide") await reexibirPost(post._id);
      await loadPosts({ showLoading: false });
      setSuccessMessage(
        action === "publish"
          ? "Post publicado com sucesso!"
          : "Post reexibido com sucesso!",
      );
    } catch (error) {
      showFeedback(getErrorMessage(error, "Nao foi possivel atualizar o status do post."));
    } finally {
      setBusyId("");
    }
  };

  const confirmAction = async () => {
    if (!confirmation) return;

    const { type, post } = confirmation;
    setBusyId(post._id);
    setConfirmation(null);

    try {
      if (type === "hide") await ocultarPost(post._id);
      if (type === "delete") await excluirPost(post._id);
      await loadPosts({ showLoading: false });
      setSuccessMessage(
        type === "hide"
          ? "Post ocultado com sucesso!"
          : "Post excluído permanentemente!",
      );
    } catch (error) {
      showFeedback(getErrorMessage(error, "Nao foi possivel concluir a acao."));
    } finally {
      setBusyId("");
    }
  };

  return (
    <div className={`admin-blog${editMode ? " admin-blog--editing" : ""}`}>
      <header className="admin-blog__header">
        <h1>EDITAR SEÇÃO</h1>
        <p>Blog</p>
      </header>

      {!editMode && (
        <div className="admin-blog__featured">
          <FeaturedPostsCarousel
            posts={publishedPosts}
            linkPosts={false}
          />
        </div>
      )}

      <section className="admin-blog__intro">
        <h2>Fique por dentro!</h2>
        <p>
          Acompanhe noticias, historias e conteudos sobre o trabalho do Instituto
          7 Geracoes.
        </p>
      </section>

      {feedback && <p className="admin-blog__feedback" role="status">{feedback}</p>}
      {editorLoading && <p className="admin-blog__state">Carregando post...</p>}
      {loading && <p className="admin-blog__state">Carregando posts...</p>}
      {!loading && loadError && <p className="admin-blog__error">{loadError}</p>}

      {!loading && !loadError && (
        <>
          <section className="admin-blog__list" aria-label="Posts do Blog">
            {visiblePosts.length === 0 && (
              <p className="admin-blog__empty">Nenhum post cadastrado ainda.</p>
            )}

            {visiblePosts.map((post) => {
              const coverUrl = getImageUrl(post.images?.[0]);
              const isBusy = busyId === post._id;

              return (
                <article className="admin-blog-card" key={post._id}>
                  <div className="admin-blog-card__media">
                    {coverUrl ? <img src={coverUrl} alt="" /> : <span>Sem imagem</span>}
                  </div>

                  <div className="admin-blog-card__body">
                    <div className="admin-blog-card__heading">
                      <h2>{post.title}</h2>
                      <div className="admin-blog-card__badges">
                        {post.featured && <span className="admin-blog-card__featured">Destaque</span>}
                        <span className={`admin-blog-card__status admin-blog-card__status--${post.status}`}>
                          {STATUS_LABELS[post.status] || post.status}
                        </span>
                      </div>
                    </div>
                    <p>{post.summary}</p>
                    <div className="admin-blog-card__meta">
                      <strong>Por {getAuthorName(post)}</strong>
                      <time dateTime={post.publishedAt || post.updatedAt}>
                        {formatDate(post.publishedAt || post.updatedAt)}
                      </time>
                    </div>

                    {editMode && (
                      <div className="admin-blog-card__actions">
                        <button
                          type="button"
                          onClick={() => openEditPost(post)}
                          disabled={isBusy || editorLoading}
                        >
                          Editar
                        </button>

                        {post.status === "draft" && (
                          <button type="button" onClick={() => runStatusAction(post, "publish")} disabled={isBusy}>
                            {isBusy ? "Publicando..." : "Publicar"}
                          </button>
                        )}
                        {post.status === "published" && (
                          <button
                            type="button"
                            onClick={() => setConfirmation({ type: "hide", post })}
                            disabled={isBusy}
                          >
                            Ocultar
                          </button>
                        )}
                        {post.status === "hidden" && (
                          <button type="button" onClick={() => runStatusAction(post, "unhide")} disabled={isBusy}>
                            {isBusy ? "Reexibindo..." : "Reexibir"}
                          </button>
                        )}
                        <button
                          type="button"
                          className="admin-blog-card__delete"
                          onClick={() => setConfirmation({ type: "delete", post })}
                          disabled={isBusy}
                        >
                          Excluir
                        </button>
                      </div>
                    )}
                  </div>
                </article>
              );
            })}
          </section>

          {editMode && (
            <button type="button" className="admin-blog__add" onClick={openNewPost}>
              Adicionar post <span aria-hidden="true">+</span>
            </button>
          )}

          {pageCount > 1 && (
            <nav className="admin-blog__pagination" aria-label="Paginacao dos posts">
              {Array.from({ length: pageCount }, (_, index) => index + 1).map((pageNumber) => (
                <button
                  type="button"
                  key={pageNumber}
                  className={pageNumber === page ? "admin-blog__page--active" : ""}
                  onClick={() => setPage(pageNumber)}
                  aria-current={pageNumber === page ? "page" : undefined}
                >
                  {pageNumber}
                </button>
              ))}
              <button
                type="button"
                onClick={() => setPage((current) => Math.min(pageCount, current + 1))}
                disabled={page === pageCount}
                aria-label="Proxima pagina"
              >
                &gt;
              </button>
            </nav>
          )}

          <FormActions
            editing={false}
            className="admin-blog__mode-actions"
            editLabel={editMode ? "Concluir" : "Editar"}
            onEdit={() => setEditMode((current) => !current)}
          />
        </>
      )}

      {editorPost !== undefined && (
        <BlogPostEditor
          post={editorPost}
          onCancel={closeEditor}
          onSave={savePost}
          saving={saving}
          submitError={submitError}
        />
      )}

      <ConfirmDialog
        open={Boolean(confirmation)}
        title={
          confirmation?.type === "delete"
            ? "Confirmar a exclusão?"
            : "Confirmar ocultação?"
        }
        description={
          confirmation?.type === "delete"
            ? `O post "${confirmation?.post?.title || "selecionado"}" será removido permanentemente.`
            : `O post "${confirmation?.post?.title || "selecionado"}" deixará de aparecer no site, mas continuará salvo no painel.`
        }
        confirmLabel={confirmation?.type === "delete" ? "Confirmar" : "Ocultar"}
        onCancel={() => setConfirmation(null)}
        onConfirm={confirmAction}
      />
      <SuccessDialog
        open={Boolean(successMessage)}
        message={successMessage}
        onClose={() => setSuccessMessage("")}
      />
    </div>
  );
}
